import { useTranslation } from 'react-i18next';
import { Box, styled, SvgIcon } from '@mui/material';
import { useGetLearnerByOrganisation } from 'api';
import {
  SvgRoadSignWarningRegular,
  TextV2,
} from '@insights-ltd/design-library/components';
import Grid from '@mui/material/Grid';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { sortChapters } from 'utils/mappers/sorting';
import { chapterNameMap } from 'components/PurchaseProfiles/ChapterCheckboxes/ChapterNameMap';
import { formatDateFromIsoString } from '@insights-ltd/design-library/utils';
import { DIALECT_MAP } from 'variables';
import { LearnerForPurchase } from 'components/PurchaseProfiles/types';
import { formatList } from 'utils/formatList';
import { LearnerErrorList } from './LearnerErrorList';
import { RemoveButton } from './RemoveButton';
import {
  getArchivedProfileType,
  getIDTLPurchaseErrorReason,
  getPIPPurchaseErrorReason,
  getPreferredDialectAndPronoun,
  isPreferredLanguageNotSupported,
} from './helper';

const AssignedChapters = styled('div')(({ theme }) => ({
  position: 'static',
  top: theme.spacing(spacingSizeMap.XS),
  left: '0',
  width: '12px',
  height: '12px',
  marginRight: theme.spacing(spacingSizeMap.XS),
  borderRadius: '50%',
  border: `2px solid ${theme.palette.orange.main}`,
}));

const PurchasedChapters = styled('div')(({ theme }) => ({
  position: 'static',
  top: theme.spacing(spacingSizeMap.XS),
  left: '0',
  width: '12px',
  height: '12px',
  marginRight: theme.spacing(spacingSizeMap.XS),
  borderRadius: '50%',
  backgroundColor: theme.palette.success.main,
}));

const ChaptersStatus = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  '& span': {
    lineHeight: '1.8rem',
  },
});

const ChaptersList = styled('div')({
  '& > p': {
    fontWeight: '600 !important',
  },
});

const StyledSvgRoadSignWarningRegular = styled(SvgRoadSignWarningRegular)(
  ({ theme }) => ({
    width: '20px',
    height: '20px',
    fill: theme.palette.orange.main,
    stroke: theme.palette.common.white,
  }),
);

const LearnerListRow = ({
  learner,
  organisationId,
  evaluatorLinkOrganisationId,
  evaluatorLinkId,
  removeCheckedItem,
  productName,
}: {
  learner: LearnerForPurchase;
  organisationId?: string;
  evaluatorLinkOrganisationId?: string;
  evaluatorLinkId?: string;
  removeCheckedItem?: (id: string) => void;
  productName?: string;
}) => {
  const {
    id,
    fullName,
    primaryEmail,
    latestEvaluation,
    purchaseTotal = 0,
    assignedChapters,
    purchasedChapters,
  } = learner;

  const { t, i18n } = useTranslation();
  const { language: locale } = i18n;
  const isClassicExplore = latestEvaluation.evaluatorType === 'CLASSIC_EXPLORE';

  const errorReason =
    (productName === 'DISCOVERY_TRANSFORMATIONAL_LEADERSHIP_PROFILE' &&
      getIDTLPurchaseErrorReason(latestEvaluation)) ||
    (productName === 'DISCOVERY_IMPACT_PROFILE' &&
      getPIPPurchaseErrorReason(latestEvaluation)) ||
    undefined;
  const archivedProfileType = getArchivedProfileType(
    latestEvaluation,
    productName,
  );
  const showsErrorRow = !!errorReason || !!archivedProfileType;

  const dialect = DIALECT_MAP.find(
    ({ value }) => value === latestEvaluation.preferredProfileDialect,
  );

  const isNotSupported = isPreferredLanguageNotSupported(dialect, productName);
  const showUnsupportedWarning =
    !showsErrorRow && !!isNotSupported && purchasedChapters.length === 0;

  const { data: learnerDetails } = useGetLearnerByOrganisation(
    id || '',
    organisationId || '',
    { enabled: showUnsupportedWarning },
  );
  const pronoun = learnerDetails?.latestEvaluation?.pronoun;

  const assignedChaptersList = formatList(
    assignedChapters
      .sort(({ name: nameA }, { name: nameB }) => sortChapters(nameA, nameB))
      .map((chapter) => t(chapterNameMap[chapter.name])),
    locale,
  );

  const purchasedChaptersList = formatList(
    purchasedChapters
      .sort((nameA, nameB) => sortChapters(nameA, nameB))
      .map((chapter) => t(chapterNameMap[chapter])),
    locale,
  );

  if (showsErrorRow) {
    return (
      <LearnerErrorList
        learnerId={id}
        organisationId={organisationId}
        evaluatorLinkOrganisationId={evaluatorLinkOrganisationId}
        evaluatorLinkId={evaluatorLinkId}
        removeCheckedItem={removeCheckedItem}
        fullName={fullName}
        primaryEmail={primaryEmail}
        latestEvaluationId={latestEvaluation.id}
        latestEvaluationType={latestEvaluation.evaluatorType}
        errorReason={errorReason}
        archivedProfileType={archivedProfileType}
      />
    );
  }

  return (
    <Grid
      container
      data-testid={`${latestEvaluation.id}-basket-item`}
      columnSpacing={0}
      rowSpacing={0}
      sx={[
        {
          alignItems: 'baseline',
        },
        (theme) => ({
          width: '100%',
          marginTop: '0',
          marginBottom: theme.spacing(spacingSizeMap.XS),
          padding: `0.75rem 1rem 0.75rem`,
          wordBreak: 'break-word',
          border: `solid 1px ${theme.palette.grey[500]}`,
          borderRadius: '4px',
          ...((assignedChapters.length === 0 || isClassicExplore) && {
            backgroundColor: theme.palette.grey[300],
            border: '0',
          }),
        }),
      ]}
    >
      <Grid
        sx={() => ({
          '& > p': {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
          paddingTop: `0 !important`,
          px: 1,
        })}
        size={3}
      >
        <TextV2 component="p" variant="bodyBold">
          {fullName}
        </TextV2>
        <TextV2 component="p" color="textSecondary">
          {primaryEmail}
        </TextV2>
        <TextV2 component="p" color="textSecondary">
          {t(
            'ui.event-management.learners.purchase-profiles.your-basket.completed-date',
            {
              completedDate: formatDateFromIsoString(
                latestEvaluation.submittedOn,
                locale,
              ),
            },
          )}
        </TextV2>
        {showUnsupportedWarning && (
          <TextV2 component="p" color="textSecondary">
            {getPreferredDialectAndPronoun(t, dialect, pronoun)}
          </TextV2>
        )}
      </Grid>
      <Grid sx={{ paddingTop: `0 !important`, px: 1 }} size={1}>
        <RemoveButton
          removeCheckedItem={removeCheckedItem}
          latestEvaluationId={latestEvaluation.id}
          t={t}
        />
      </Grid>
      <Grid
        sx={(theme) => ({
          padding: `0 ${theme.spacing(spacingSizeMap.S)} !important`,
        })}
        size={isClassicExplore ? 8 : 4}
      >
        {assignedChapters.length > 0 && isClassicExplore ? (
          <TextV2 component="span" color="error">
            {t(
              'ui.event-management.learners.purchase-profiles.your-basket.migrated-profile',
            )}
          </TextV2>
        ) : (
          <>
            <ChaptersStatus>
              <AssignedChapters />
              <span>
                &nbsp;
                {t('ui.event-management.events.assign-chapters.assigned')}
              </span>
            </ChaptersStatus>
            <ChaptersList>
              <TextV2
                component="span"
                data-testid={`${latestEvaluation.id}-assign-chapters`}
              >
                {assignedChaptersList}
              </TextV2>
            </ChaptersList>
          </>
        )}
      </Grid>
      <Grid
        sx={(theme) => ({
          padding: `0 ${theme.spacing(spacingSizeMap.S)} !important`,
        })}
        size={3}
      >
        {purchasedChapters.length > 0 && (
          <>
            <ChaptersStatus>
              <PurchasedChapters />
              <span>
                &nbsp;
                {t('ui.event-management.events.assign-chapters.purchased')}
              </span>
            </ChaptersStatus>
            <ChaptersList>
              <TextV2
                component="p"
                data-testid={`${latestEvaluation.id}-purchase-chapters`}
              >
                {purchasedChaptersList}
              </TextV2>
            </ChaptersList>
          </>
        )}
        {showUnsupportedWarning && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <SvgIcon>
              <StyledSvgRoadSignWarningRegular />
            </SvgIcon>
            <TextV2
              component="span"
              variant="bodyBold"
              data-testid={`${latestEvaluation.id}-assign-chapters`}
            >
              {t(
                'ui.event-management.events.assign-chapters.unsupported-preferred-language',
              )}
            </TextV2>
          </Box>
        )}
      </Grid>
      {!isClassicExplore && (
        <Grid
          sx={{
            paddingTop: `0 !important`,
            px: 1,
          }}
          size={1}
        >
          <TextV2
            component="span"
            color="textSecondary"
            variant="bodyBold"
            data-testid={`${latestEvaluation.id}-sub-total`}
          >
            {purchaseTotal}&nbsp;
            {t('ui.event-management.events.assign-chapters.summary-box.units')}
          </TextV2>
        </Grid>
      )}
    </Grid>
  );
};

export default LearnerListRow;
